import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Alert,
} from '@mui/material';
import httpclient from '../../../Utils';

const AddEditDialog = ({ open, onClose, permission, mode, menuList = [] }) => {
  const [formData, setFormData] = useState({
    name: '',
    menu: '',
    status: 1,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Populate form when editing
  useEffect(() => {
    if (mode === 'edit' && permission) {
      setFormData({
        name: permission.name || '',
        menu: permission.menu || '',
        status: permission.status !== undefined ? permission.status : 1,
      });
    } else {
      setFormData({
        name: '',
        menu: '',
        status: 1,
      });
    }
    setError(null);
  }, [mode, permission, open]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = () => {
    if (!formData.name || !formData.menu) {
      setError('Please fill all required fields');
      return;
    }

    setLoading(true);
    setError(null);

    if (mode === 'edit') {
      // PUT API call for editing - using /permissions/{id}
      const updateData = {
        name: formData.name,
        menu: formData.menu,
        status: formData.status
      };
      
      httpclient
        .put(`permissions/${permission.id}`, updateData)
        .then(({ data }) => {
          if (data.responseCode === 200 && data.success) {
            setLoading(false);
            onClose(true); // Refresh the list
          } else {
            setError(data.message || 'Failed to update permission');
            setLoading(false);
          }
        })
        .catch((err) => {
          setError(err.response?.data?.message || 'An error occurred while updating');
          setLoading(false);
        });
    } else {
      // POST API call for creating new permission
      const createData = {
        name: formData.name,
        menu: formData.menu,
        status: formData.status
      };
      
      httpclient
        .post('permissions', createData)
        .then(({ data }) => {
          if (data.responseCode === 200 && data.success) {
            setLoading(false);
            onClose(true); // Refresh the list
          } else {
            setError(data.message || 'Failed to create permission');
            setLoading(false);
          }
        })
        .catch((err) => {
          setError(err.response?.data?.message || 'An error occurred while creating');
          setLoading(false);
        });
    }
  };

  return (
    <Dialog open={open} onClose={() => onClose(false)} maxWidth="sm" fullWidth>
      <DialogTitle>
        {mode === 'edit' ? 'Edit Permission' : 'Add New Permission'}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          
          {/* Menu Dropdown - First field */}
          <FormControl fullWidth required>
            <InputLabel>Menu</InputLabel>
            <Select
              name="menu"
              value={formData.menu}
              onChange={handleChange}
              label="Menu"
            >
              {menuList.map((menu) => (
                <MenuItem key={menu.id} value={menu.name}>
                  {menu.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Permission Name - Second field */}
          <TextField
            label="Permission Name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            fullWidth
            required
            placeholder="e.g., List Admin, Create Role"
          />

          {/* Status Dropdown - Third field */}
          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              name="status"
              value={formData.status}
              onChange={handleChange}
              label="Status"
            >
              <MenuItem value={1}>Active</MenuItem>
              <MenuItem value={0}>Inactive</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose(false)}>Cancel</Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          color="primary" 
          disabled={loading}
        >
          {loading ? 'Saving...' : mode === 'edit' ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddEditDialog;